import { gql } from '@apollo/client'
import { useEffect } from 'react'
import { useChatGetUserQuery } from '../../../generated/graphql'
import useAuthClaims from '../../../hooks/useAuthClaims'
import { useAppDispatch, useAppSelector } from '../../../store'
import { HLO } from '../../../types/global'
import useLocalization from '../../localization/hooks/useLocalization'
import {
  selectChatDrawerOpen,
  selectChatToUserId,
  setChatToUserId,
  toggleChat,
} from '../slices/chatDrawerSlice'
import getChatVariant from '../util/getChatVariant'
import { ChatDrawerProps } from '../views/ChatDrawer'
import ChatRoomList from '../views/ChatRoomList'

const GET_USER = gql`
  query ChatGetUser($userId: uuid!) {
    user_by_pk(id: $userId) {
      person {
        name
      }

      role {
        name
      }
    }
  }
`

const ChatRoomListContainer = () => {
  const dispatch = useAppDispatch()
  const { language } = useLocalization()
  const { claims } = useAuthClaims()
  const open = useAppSelector(selectChatDrawerOpen)
  const toUserId = useAppSelector(selectChatToUserId)

  const { data, error, loading } = useChatGetUserQuery({
    skip: !open || !toUserId,
    variables: {
      userId: toUserId,
    },
  })

  const fromUser = claims
    ? {
        id: claims.id,
        role: { name: claims.role as HLO.Role },
      }
    : undefined

  const toUser: ChatDrawerProps['toUser'] | undefined =
    toUserId && data && data.user_by_pk
      ? data.user_by_pk && {
          id: toUserId,
          person: {
            name: data.user_by_pk.person.name || undefined,
          },
          role: { name: data.user_by_pk.role.name as HLO.Role },
        }
      : undefined

  let variant = claims && getChatVariant(claims.role, toUserId)

  const handleRoomChange = (value: string | undefined) => {
    dispatch(setChatToUserId(value))
  }

  const handleHideDrawer = () => {
    dispatch(setChatToUserId(undefined))
    dispatch(toggleChat())
  }

  const handleToggleChat = (userId: string | undefined = undefined) => {
    dispatch(toggleChat())

    if (typeof userId === 'string') {
      dispatch(setChatToUserId(userId))
    }
  }

  useEffect(() => {
    if (open && !toUserId && variant === 'user') {
      if (fromUser) {
        dispatch(setChatToUserId(fromUser.id))
      }
    }
  })

  return (
    <>
      {fromUser && variant === 'room-list' && (
        <ChatRoomList
          currentUserId={fromUser.id}
          language={language}
          onSelect={handleRoomChange}
          selected={toUser?.id}
        />
      )}
    </>
  )
}

export default ChatRoomListContainer
